import { Routes, Route, useLocation } from "react-router-dom"
import { Suspense, useMemo, lazy } from "react"
import { MainSkeleton } from "@/features/views/components/MainSkeleton"

// TodayPage stays eager — it's the default route (/) for fastest first paint
import { TodayPage } from "@/features/tasks/pages/TodayPage"

// S4: Lazy-loaded pages — split into separate chunks, loaded on navigation.
// Each page loads only when the user navigates to it.
const InboxPage = lazy(() => import("@/features/views/pages/InboxPage").then(m => ({ default: m.InboxPage })))
const UpcomingPage = lazy(() => import("@/features/views/pages/UpcomingPage").then(m => ({ default: m.UpcomingPage })))
const SomedayPage = lazy(() => import("@/features/views/pages/SomedayPage").then(m => ({ default: m.SomedayPage })))
const AnydayPage = lazy(() => import("@/features/views/pages/AnydayPage").then(m => ({ default: m.AnydayPage })))
const LogbookPage = lazy(() => import("@/features/views/pages/LogbookPage").then(m => ({ default: m.LogbookPage })))
const TrashPage = lazy(() => import("@/features/views/pages/TrashPage").then(m => ({ default: m.TrashPage })))
const LaterProjectsPage = lazy(() => import("@/features/views/pages/LaterProjectsPage").then(m => ({ default: m.LaterProjectsPage })))
const SearchPage = lazy(() => import("@/features/views/pages/SearchPage").then(m => ({ default: m.SearchPage })))
const RecurringPage = lazy(() => import("@/features/recurrence/RecurringPage").then(m => ({ default: m.RecurringPage })))
const RoutinesPage = lazy(() => import("@/features/routines/RoutinesPage").then(m => ({ default: m.RoutinesPage })))
const ProjectPage = lazy(() => import("@/features/views/pages/ProjectPage").then(m => ({ default: m.ProjectPage })))
const ListPage = lazy(() => import("@/features/views/pages/ListPage").then(m => ({ default: m.ListPage })))
const AreaPage = lazy(() => import("@/features/views/pages/AreaPage").then(m => ({ default: m.AreaPage })))
const TagPage = lazy(() => import("@/features/views/pages/TagPage").then(m => ({ default: m.TagPage })))
const NotFoundPage = lazy(() => import("@/features/views/pages/NotFoundPage").then(m => ({ default: m.NotFoundPage })))
const TaskRedirectPage = lazy(() => import("@/features/views/pages/TaskRedirectPage").then(m => ({ default: m.TaskRedirectPage })))
const DndTestPage = lazy(() => import("@/features/dnd-test/DndTestPage").then(m => ({ default: m.DndTestPage })))
const EditorTestPage = lazy(() => import("@/features/editor/EditorTestPage").then(m => ({ default: m.EditorTestPage })))
const ChatComponentsPage = lazy(() => import("@/features/ai-chat/chat-components/ChatComponentsPage").then(m => ({ default: m.ChatComponentsPage })))
const DevComponentsPage = lazy(() => import("@/features/dev/DevComponentsPage").then(m => ({ default: m.DevComponentsPage })))
const PlaceholderPage = lazy(() => import("@/features/placeholder/PlaceholderPage").then(m => ({ default: m.PlaceholderPage })))
const InvitesPage = lazy(() => import("@/features/invites/InvitesPage").then(m => ({ default: m.InvitesPage })))

// Preload functions — call on hover/focus on sidebar links for instant navigation.
export const preloadInbox = () => import("@/features/views/pages/InboxPage")
export const preloadUpcoming = () => import("@/features/views/pages/UpcomingPage")
export const preloadSomeday = () => import("@/features/views/pages/SomedayPage")
export const preloadAnyday = () => import("@/features/views/pages/AnydayPage")
export const preloadLogbook = () => import("@/features/views/pages/LogbookPage")
export const preloadTrash = () => import("@/features/views/pages/TrashPage")
export const preloadSearch = () => import("@/features/views/pages/SearchPage")

/**
 * Top-level route definitions.
 * Each route maps to a feature page component.
 * Auth is handled by DatabaseProvider on any route — no dedicated /signin.
 */
export function AppRoutes() {
  const { pathname } = useLocation()
  const skeletonVariant = useMemo(() => {
    if (/^\/(project|list)\//.test(pathname)) return "container" as const
    if (/^\/area\//.test(pathname)) return "area" as const
    return "default" as const
  }, [pathname])

  return (
    <Suspense fallback={<MainSkeleton variant={skeletonVariant} />}>
      <Routes>
        {/* Today is the default view */}
        <Route path="/" element={<TodayPage />} />
        <Route path="/today" element={<TodayPage />} />

        {/* Static views */}
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/upcoming" element={<UpcomingPage />} />
        <Route path="/anyday" element={<AnydayPage />} />
        <Route path="/someday" element={<SomedayPage />} />
        <Route path="/logbook" element={<LogbookPage />} />
        <Route path="/trash" element={<TrashPage />} />
        <Route path="/later-projects" element={<LaterProjectsPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/recurring" element={<RecurringPage />} />
        <Route path="/routines" element={<RoutinesPage />} />

        {/* Dynamic views — container detail pages */}
        <Route path="/project/:id" element={<ProjectPage />} />
        <Route path="/list/:id" element={<ListPage />} />
        <Route path="/area/:id" element={<AreaPage />} />

        {/* Stubs — not yet implemented */}
        <Route
          path="/settings"
          element={<PlaceholderPage title="Settings" />}
        />
        <Route path="/invites" element={<InvitesPage />} />
        <Route path="/tags" element={<PlaceholderPage title="Tags" />} />
        <Route path="/tag/:tag" element={<TagPage />} />

        {/* Universal task deep link — resolves to correct view */}
        <Route path="/task/:id" element={<TaskRedirectPage />} />

        {/* Test pages */}
        <Route path="/dnd-test" element={<DndTestPage />} />
        <Route path="/editor-test" element={<EditorTestPage />} />
        <Route path="/chat-components" element={<ChatComponentsPage />} />
        <Route path="/dev/components" element={<DevComponentsPage />} />

        {/* Catch-all — show 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}
